import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { Box, Typography, Paper, Divider, Grid, useTheme } from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController,
} from "chart.js";
import moment from "moment";
import Table from "./Table";
import Options from "./Options";

// רישום הרכיבים לגרסה החדשה
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LineController,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler,
);

function Weight(props) {
  const theme = useTheme();
  const [filterProps, setFilterProps] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  // סינון נתונים - העברתי ל-useMemo לאופטימיזציה
  const measurmentFiltered = useMemo(() => {
    const list = props.selectedPatient
      ? props.weightResults?.filter(
          (a) => a.custid == props.selectedPatient?.custid,
        )
      : props.weightResults || [];

    let filtered = [...list];
    if (filterProps?.filterSelected === "lastMonth") {
      filtered = filtered.filter(
        (m) =>
          new Date(m.devdatetime) > moment().subtract(1, "months").toDate(),
      );
    } else if (filterProps?.filterSelected === "betweenDates") {
      filtered = filtered.filter(
        (m) =>
          new Date(m.devdatetime) > filterProps?.fromDate &&
          new Date(m.devdatetime) < filterProps?.untilDate,
      );
    } else if (!filterProps?.filterSelected) {
      filtered = filtered.slice(0, 10);
    }
    return filtered.sort((b, a) => a.internalcounter - b.internalcounter);
  }, [props.weightResults, props.selectedPatient, filterProps]);

  const pages = Math.ceil(measurmentFiltered.length / 10);
  const toShow = measurmentFiltered.slice(
    (currentPage - 1) * 10,
    (currentPage - 1) * 10 + 10,
  );

  const weights = toShow.map((w) => parseInt(w.weightdata));

  // הגדרות המידע לגרף (מותאם לגרסה החדשה)
  const data = {
    labels: toShow.map((a) => moment(a.devdatetime).format("DD/MM/YY HH:mm")),
    datasets: [
      {
        label: 'משקל (ק"ג)',
        type: "line",
        data: toShow.map((a) => a.weightdata),
        fill: true,
        borderColor: "#f26322",
        backgroundColor: "rgba(242, 99, 34, 0.2)",
        tension: 0.4, // במקום lineTension
        pointRadius: 4,
        pointBackgroundColor: "#f26322",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
    },
    scales: {
      y: {
        // שינוי מ-yAxes
        type: "linear",
        position: "right",
        beginAtZero: false, // משקל עדיף לא להתחיל מ-0 כדי לראות שינויים
        ticks: {
          color: theme.palette.text.secondary,
        },
        max: weights.length ? Math.max(...weights) + 10 : 100,
      },
      x: {
        // שינוי מ-xAxes
        ticks: {
          color: theme.palette.text.secondary,
        },
      },
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1150px",
        margin: "0 auto",
        p: { xs: 2, md: 4 },
        direction: "rtl",
      }}
    >
      <Typography
        variant="h4"
        sx={{ fontWeight: 800, mb: 4, textAlign: "left" }}
      >
        מעקב משקל
      </Typography>

      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
        {measurmentFiltered.length === 0 ? (
          <Typography variant="h6" sx={{ textAlign: "center", py: 5 }}>
            אין נתונים להצגה
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Options updateChart={(p) => setFilterProps(p)} />

            <Grid container spacing={4}>
              <Grid item xs={12} lg={6}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 700, textAlign: "left" }}
                >
                  טבלת מדידות
                </Typography>
                <Table
                  kind="weight"
                  measurment={toShow}
                  pages={pages}
                  currentPage={currentPage}
                  updatePageNumber={(i) => setCurrentPage(i)}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    textAlign: "left",
                  }}
                >
                  גרף משקל
                </Typography>
                <Box
                  sx={{
                    height: "400px",
                    bgcolor: "#fafafa",
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #eee",
                  }}
                >
                  <Bar data={data} options={options} />
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  weightResults: state.weightResults,
  selectedPatient: state.selectedPatient,
});

export default connect(mapStateToProps)(Weight);
